import { useStore } from '@tanstack/react-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { useAppForm } from '@hooks/form'
import useErrorMessage from '@hooks/use-error-message'

import { useMutationPasswordResetCreate } from '@api/password-reset/password-reset.mutations'

import FieldErrors from '@/components/form/field-errors'

export const Route = createFileRoute('/session/password-reset/')({
  component: RouteComponent,
})

const schema = z.object({
  email: z.email({ message: 'form.error.email' }),
})

function RouteComponent() {
  const { t } = useTranslation()
  const { mutate, isPending } = useMutationPasswordResetCreate()
  const navigate = useNavigate()
  const errorMessage = useErrorMessage()

  const form = useAppForm({
    defaultValues: { email: '' },
    validators: { onSubmit: schema },
    onSubmit: ({ value, formApi }) => {
      mutate(value, {
        onSuccess: () => navigate({ from: Route.fullPath, to: './link-sent' }),
        onError: (error) => {
          const message = errorMessage({ error })

          formApi.setErrorMap({
            onSubmit: {
              form: message,
              fields: {},
            },
          })
        },
      })
    },
  })

  const errors = useStore(form.store, (state) =>
    state.errors.filter((e) => typeof e === 'string'),
  )

  return (
    <>
      <div className="flex items-center gap-2">
        <Route.Link to="/session/login">
          <ArrowLeft className="size-6" strokeWidth={2.5} />
        </Route.Link>
        <h1 className="text-lg font-bold">
          {t('session.password-reset.ask.title')}
        </h1>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-4"
      >
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label={t('session.password-reset.ask.fields.email.label')}
              placeholder={t(
                'session.password-reset.ask.fields.email.placeholder',
              )}
            />
          )}
        </form.AppField>
        {!!errors.length && <FieldErrors errors={errors} />}
        <form.AppForm>
          <form.SubmitButton pending={isPending} variant="primary" size="sm">
            <div className="flex items-center gap-1">
              <span>{t('session.password-reset.ask.submit')}</span>
              <ArrowRight className="size-4 rounded-md" />
            </div>
          </form.SubmitButton>
        </form.AppForm>
      </form>
    </>
  )
}
